import * as classnames from "classnames";
import { UnregisterCallback } from "history";
import { isEqual } from "lodash";
import * as React from "react";
import { Prompt } from "react-router";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Split from "react-split";
import { toast } from "react-toastify";

import { SwitchFrontendTemplate } from "@components/user/modals/switchFrontendTemplate/SwitchFrontendTemplate";

import {
  ApplicantChallengeHeader,
  Button,
  Icon,
  TadaMini,
  Tooltip,
  CopyBox,
  JumpTo,
  Msg,
  HotkeyHandler,
} from "@shared/components";
import {
  UseSendScreenshotApplicantActionMutationResult,
  UseUpdateApplicantsExamResultsMutationResult,
} from "@shared/hooks/query";
import {
  ApplicantExamModel,
  ApplicantChallengeContextModel,
  ApplicantEditorContextModel,
} from "@shared/models";
import { bowser } from "@shared/services/browserSupport";
import ChallengeEditor from "@shared/services/challengeEditor";
import {
  ChallengeResultStatus,
  ProgrammingLanguage,
  ChallengeStyle,
  ChallengeTakenBy,
} from "@shared/services/enums";
import {
  MESSAGE_EVENT_READY,
  MESSAGE_EVENT_HAS_UNSAVED,
  MESSAGE_EVENT_SAVED,
  MESSAGE_EVENT_QUIZ_MOVED,
  MESSAGE_EVENT_CACHE_CLEAR,
  MESSAGE_EVENT_TEST_RESULT,
  MESSAGE_EVENT_RUN_TESTS,
  MESSAGE_NOTIFY_ERROR,
  MESSAGE_PASTED,
  MessageHasUnsaved,
  MessageMoved,
  MESSAGE_QUIZ_PASTED,
  MESSAGE_EVENT_RELOAD,
} from "@shared/services/events";
import history from "@shared/services/history";
import { getScreenshotHotKeys } from "@shared/services/hotkey";
import { intercom } from "@shared/services/intercom";
import { canRecordKeyEvents } from "@shared/services/keyEvents";
import Logger from "@shared/services/logger";
import Message from "@shared/services/message";
import {
  LAST_SCORE_KEY,
  sessionStorage,
  SHOW_COMPLETED_MODAL_KEY,
} from "@shared/services/storage";
import { pluckApplicantChallengeURL, isQuizURL } from "@shared/services/url";
import { canTakeWebcamScreenshots } from "@shared/services/webcamEvents";

import Completed from "../../../modals/completed/Completed";
import ResetChallenge from "../../../modals/resetChallenge/ResetChallenge";
import SetEnvironment from "../../../modals/setEnvironment/SetEnvironment";
import StartChallenge from "../../../modals/startChallengeWalkThrough/StartChallengeWalkThrough";
import StartChallengeWebcam from "../../../modals/startChallengeWalkThrough/StartChallengeWalkThroughWebcam";
import SubmitChallenge from "../../../modals/submitChallenge";
import SwitchLanguage from "../../../modals/switchLanguage/SwitchLanguage";
import SwitchTakenBy from "../../../modals/switchTakenBy/SwitchTakenBy";
import { EditorAppealForm } from "../editorAppealForm/EditorAppealForm";
import { SubmitExamAlert } from "../submitExamAlert/SubmitExamAlert";
import { KeyEventHandler } from "./partial";
import { WebcamEventHandler } from "./partial/webcamEventHandler/WebcamEventHandler";

/**
 * Prop interface
 */
export interface BaseProps {
  challengeId: number;
  currentPage?: number;
  tutorial?: boolean;
  exam: ApplicantExamModel;
  challengeContext: ApplicantChallengeContextModel;
  challengeEditorContext: ApplicantEditorContextModel;
  isUpdatingAppealComment: boolean;
  isUpdateAppealCommentSuccess: boolean;
  isUpdateAppealCommentError: boolean;

  getExam: () => void;
  startChallenge: (payload: {}) => void;
  resetChallenge: (params: {}) => void;
  saveChallenge: (payload: {}, params?: {}) => void;
  switchLanguage: (payload: {}, params: {}) => void;
  switchFrontendTemplate: (payload: {}, params: {}) => void;
  updateEnvVars: (payload: {}, params: {}) => void;
  submitExam: () => void;
  removeNotification: (payload: {}) => void;
  getChallengeContext: (params: {}) => void;
  removeChallengeContext: () => void;
  setEditorHasUnsaved: (payload: {}) => void;
  setEditorReady: () => void;
  setEditorReload: () => void;
  updateAppealComment: (payload: {}, params: {}) => void;
  switchTakenBy: (payload: {}, params: {}) => void;
  history?: History;
}

export type ExternalProps = BaseProps & RouteComponentProps;

export type PasteEventType = {
  resultId: number;
  pastedText: string;
} & ({ filename: string } | { questionVersionId: number });

export type InjectedProps = {
  applicantIncludeAppealCommentLogAction: (payload?: {}) => void;
  sendPasteEvent: (pasteEvent: PasteEventType) => void;
};

interface HookProps {
  isSubmittingChallenge: boolean;
  submitChallenge: UseUpdateApplicantsExamResultsMutationResult;
  sendScreenshotEvent: UseSendScreenshotApplicantActionMutationResult;
  isWebcamEnabled: boolean;
  hasVerifiedLiveness: boolean;
}

type EditorMainProps = ExternalProps & InjectedProps & HookProps;

/**
 * State interface
 */
export interface EditorMainState {
  isManualSubmit: boolean;
  isOpenStart: boolean;
  isOpenSwitchLanguage: boolean;
  isOpenSwitchFrontendTemplate: boolean;
  isOpenEnvVars: boolean;
  isOpenReset: boolean;
  isOpenSubmit: boolean;
  isOpenAppealComment: boolean;
  isSwitchingLanguage: boolean;
  isOpenSwitchTakenBy: boolean;
  isOpenCompleted: boolean;
  isShowCodeUpdated: boolean;
  isReset: boolean;
  testResult: TestResult;
  showTada: boolean;
  appealComment: string;
  switchTakenByTo: ChallengeTakenBy;
  showCompletedType: string;
  askingUnsaved: boolean;
  onQuiz: boolean;
}

interface TestResult {
  success: number;
  total: number;
  highScore: number;
}

/**
 * Page component
 */
class EditorMain extends React.Component<EditorMainProps, EditorMainState> {
  private prevTick = 0;
  private animationFrameId: number | null;
  private challengeEditor?: ChallengeEditor;
  private unlisten: UnregisterCallback | undefined;
  public iframe: HTMLIFrameElement | null;

  constructor(props: EditorMainProps) {
    super(props);

    const { challengeId, exam } = this.props;

    const cachedTestResult = sessionStorage.getItem(
      `${LAST_SCORE_KEY}.${exam.urlToken}.${challengeId}`,
    );

    this.state = {
      isManualSubmit: false,
      isOpenEnvVars: false,
      isOpenSwitchLanguage: false,
      isOpenSwitchFrontendTemplate: false,
      isOpenStart: false,
      isOpenReset: false,
      isOpenSubmit: false,
      isOpenAppealComment: false,
      isSwitchingLanguage: false,
      isOpenSwitchTakenBy: false,
      isOpenCompleted: false,
      isShowCodeUpdated: false,
      isReset: false,
      showTada: false,
      testResult: cachedTestResult
        ? cachedTestResult
        : {
            success: 0,
            total: 0,
            highScore: 0,
          },
      appealComment: "",
      switchTakenByTo: ChallengeTakenBy.NotSelected,
      showCompletedType: "",
      askingUnsaved: false,
      onQuiz: isQuizURL(window.location.href),
    };
  }

  public UNSAFE_componentWillMount() {
    const { exam, challengeId, getChallengeContext } = this.props;

    if (exam.isInProgress()) {
      this.setState({ isOpenStart: exam.canChallengeStart(challengeId) });

      if (exam.isChallengeStarted(challengeId)) {
        const challengeResult = exam.getResultByChallengeId(challengeId);
        getChallengeContext(challengeResult.id);
      }
    }
  }

  public componentDidMount() {
    this.challengeEditor = new ChallengeEditor("code-editor-iframe");
    this.challengeEditor.addListener(MESSAGE_EVENT_SAVED, this.onSaved);
    this.challengeEditor.addListener(
      MESSAGE_EVENT_HAS_UNSAVED,
      this.onHasUnsaved,
    );
    this.challengeEditor.addListener(MESSAGE_EVENT_QUIZ_MOVED, this.onMovePage);
    this.challengeEditor.addListener(
      MESSAGE_EVENT_READY,
      this.props.setEditorReady,
    );
    this.challengeEditor.addListener(
      MESSAGE_EVENT_RELOAD,
      this.props.setEditorReload,
    );
    this.challengeEditor.addListener(
      MESSAGE_EVENT_CACHE_CLEAR,
      this.onEditorCacheClearComplete,
    );
    this.challengeEditor.addListener(
      MESSAGE_EVENT_TEST_RESULT,
      this.onTestResult,
    );
    this.challengeEditor.addListener(
      MESSAGE_EVENT_RUN_TESTS,
      this.onCloseModal,
    );
    this.challengeEditor.addListener(
      MESSAGE_NOTIFY_ERROR,
      this.onEditorNotifyError,
    );
    this.challengeEditor.addListener(MESSAGE_PASTED, this.onEditorPasted);
    this.challengeEditor.addListener(
      MESSAGE_QUIZ_PASTED,
      this.onQuizEditorPasted,
    );

    const showCompletedType =
      sessionStorage.getItem(SHOW_COMPLETED_MODAL_KEY) || "";
    if (showCompletedType) {
      this.setState({ isOpenCompleted: true, showCompletedType }, () =>
        sessionStorage.removeItem(SHOW_COMPLETED_MODAL_KEY),
      );
    }

    // Starts periodic polling strategy to retrieve last saved code for CLI challenger
    this.animationFrameId = window.requestAnimationFrame(this.tick);

    this.unlisten = history.listen((location, action) => {
      this.setState({ onQuiz: isQuizURL(location.pathname) });
    });
    window.addEventListener("popstate", this.onPopState);
    window.addEventListener("beforeunload", this.onBeforeUnload);
    this.props.applicantIncludeAppealCommentLogAction();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: EditorMainProps) {
    const {
      exam: prevExam,
      challengeId: prevChallengeId,
      challengeContext: prevChallengeContext,
      challengeEditorContext: prevEditorContext,
    } = this.props;
    const {
      exam: nextExam,
      challengeId: nextChallengeId,
      challengeContext: nextChallengeContext,
      challengeEditorContext: nextEditorContext,
    } = nextProps;

    // Handle switching challenge.
    if (prevChallengeId !== nextChallengeId) {
      if (nextExam.isInProgress()) {
        this.props.removeChallengeContext();

        const cachedTestResult = sessionStorage.getItem(
          `${LAST_SCORE_KEY}.${nextExam.urlToken}.${nextChallengeId}`,
        );

        this.setState(
          {
            testResult: cachedTestResult
              ? cachedTestResult
              : {
                  success: 0,
                  total: 0,
                  highScore: 0,
                },
            isOpenAppealComment: false,
          },
          () => {
            if (!nextExam.isChallengeStarted(nextChallengeId)) {
              // For displaying a confirmation for challenge start.
              this.setState({
                isOpenStart: nextExam.canChallengeStart(nextChallengeId),
              });
            } else {
              // For fetching challenge context.
              const challengeResult =
                nextExam.getResultByChallengeId(nextChallengeId);
              nextProps.getChallengeContext(challengeResult.id);
            }
          },
        );
      }
    }

    // Handle update state with same challenge in.
    if (!isEqual(prevExam, nextExam)) {
      if (nextExam.isInProgress()) {
        // For handling just after start challenge.
        const challengeResult =
          nextExam.getResultByChallengeId(nextChallengeId);
        if (
          !prevExam.isChallengeStarted(prevChallengeId) &&
          nextExam.isChallengeStarted(nextChallengeId)
        ) {
          // For fetching challenge context.
          nextProps.getChallengeContext(challengeResult.id);
        }

        const currentFileVersions =
          this.props.exam.getResultByChallengeId(prevChallengeId)?.answers
            ?.fileVersions;
        const nextFileVersions = challengeResult?.answers?.fileVersions;

        if (
          currentFileVersions &&
          nextFileVersions &&
          !isEqual(currentFileVersions, nextFileVersions)
        ) {
          this.setState({ isShowCodeUpdated: true }, () => {
            if (this.challengeEditor) {
              this.challengeEditor.clearCache();
            }
          });
        }
      }

      // Handling exam/challenge auto submitted
      if (
        !prevExam.isChallengeFinished(prevChallengeId) &&
        nextExam.isChallengeFinished(nextChallengeId)
      ) {
        if (this.animationFrameId) {
          window.cancelAnimationFrame(this.animationFrameId);
        }
        this.setState({
          isOpenEnvVars: false,
          isOpenSwitchLanguage: false,
          isOpenSwitchFrontendTemplate: false,
          isOpenStart: false,
          isOpenReset: false,
          isOpenSubmit: this.state.isManualSubmit,
          isOpenAppealComment: false,
          isOpenSwitchTakenBy: false,
          isOpenCompleted: false,
          isShowCodeUpdated: false,
        });
      }
    }

    // Handle editor context change
    if (
      !isEqual(prevChallengeContext, nextChallengeContext) ||
      !isEqual(prevEditorContext, nextEditorContext)
    ) {
      if (
        (!prevEditorContext.editorReady || !prevEditorContext.contextReady) &&
        nextEditorContext.editorReady &&
        nextEditorContext.contextReady
      ) {
        if (this.challengeEditor) {
          this.challengeEditor.sendEditorContext(
            nextChallengeContext.data,
            Message.locale,
            nextProps.currentPage,
          );
        }
        this.setState({
          appealComment:
            nextExam.getResultByChallengeId(nextChallengeId).appealComment ||
            "",
        });
      }
    }
  }

  public componentDidUpdate(
    prevProps: EditorMainProps,
    prevState: EditorMainState,
  ) {
    // Reset session storage value on the quiz editor
    if (prevState.onQuiz && !this.state.onQuiz) {
      this.challengeEditor?.reset();
    }

    if (
      this.props.challengeId ||
      prevProps.challengeId !== this.props.challengeId
    ) {
      this.handleIntercomPositionUpdate();
    }
  }

  public shouldComponentUpdate(
    nextProps: EditorMainProps,
    nextState: EditorMainState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public componentWillUnmount() {
    if (this.challengeEditor) {
      this.challengeEditor.destroy();
    }
    if (this.animationFrameId) {
      window.cancelAnimationFrame(this.animationFrameId);
    }
    if (this.unlisten) {
      this.unlisten();
    }
    this.props.removeChallengeContext();
    window.removeEventListener("popstate", this.onPopState);

    // only triggers if the user switches to another url or closes the browser
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  public render() {
    const rootStyle = classnames("code-editor-main");

    const {
      exam = {} as ApplicantExamModel,
      challengeId,
      switchLanguage,
      updateEnvVars,
      challengeContext,
      challengeEditorContext,
      tutorial,
      isUpdatingAppealComment,
      isUpdateAppealCommentSuccess,
      isUpdateAppealCommentError,
      hasVerifiedLiveness,
    } = this.props;

    const {
      isOpenEnvVars,
      isOpenStart,
      isOpenSwitchLanguage,
      isOpenSwitchFrontendTemplate,
      isOpenReset,
      isOpenSubmit,
    } = this.state;

    const challengeResult = exam.getResultByChallengeId(challengeId);
    const challenge = exam.getChallengeByChallengeId(challengeId);
    const appealTitleClassName = classnames({
      "code-editor-main__appeal-form-title":
        challenge.style !== ChallengeStyle.AI,
      "code-editor-main__appeal-form-title__ai":
        challenge.style === ChallengeStyle.AI,
    });

    const challengeFinished = exam.isChallengeFinished(challengeId) && (
      <div className="code-editor-main__submitted-container">
        {exam.areAllChallengesFinished() && (
          <SubmitExamAlert
            submitExam={this.props.submitExam}
            className="code-editor-main__submitted__alert"
          />
        )}
        <div className="code-editor-main__submitted-container-top">
          <div className="code-editor-main__submitted">
            <h2 className="code-editor-main__submitted-title">
              <Icon type={"ban"} />
              <Msg id="editor.challengeSubmitted.title" />
            </h2>
            <p>
              <Msg id="editor.challengeSubmitted.description" />
            </p>
          </div>
          <Button
            route={{
              pathname: `/${tutorial ? "tutorial" : exam.org.name}/exams/${
                exam.urlToken
              }/challenges`,
            }}
            pageReload={ChallengeStyle.shouldUseVsCodeEditor(challenge.style)}
            ariaLabel="Back To Challenge List"
          >
            <Msg id={"button.backToChallengeList"} />
          </Button>
        </div>
      </div>
    );

    const editorClassName = classnames("code-editor__editor", {
      "is-code-explanation-open":
        this.state.isOpenAppealComment &&
        !exam.isChallengeFinished(challengeId),
    });
    return (
      <div className={rootStyle} aria-expanded={this.state.isOpenAppealComment}>
        {!exam.isChallengeFinished(challengeId) && (
          <Prompt
            when={
              (challengeEditorContext.hasUnsaved &&
                !this.state.isSwitchingLanguage) ||
              (ChallengeStyle.hasAppealComment(challenge.style) &&
                (challengeResult.appealComment || "") !==
                  this.state.appealComment)
            }
            /**
             * params - Location
             * action - Values can be either "POP" or "PUSH"
             */
            message={(params, action) =>
              // Return a string to show a prompt to the user or true to allow the transition.
              // POP triggers when user clicks on browser back or forward
              // button AND if the incoming page is in the same url
              action === "POP" ||
              pluckApplicantChallengeURL(params.pathname) !==
                pluckApplicantChallengeURL(window.location.pathname)
                ? Message.getMessageByKey("navigation.confirm")
                : true
            }
          />
        )}
        <div className="code-editor-main__header-wrapper">
          <ApplicantChallengeHeader
            headerLogoURL={exam.org.imageUrl}
            examName={exam.exam.name}
            challenge={challenge}
            hasMenu={ChallengeStyle.isCodingTypeChallenge(challenge.style)}
            hasSwitchLanguage={challenge.isProgrammingChallenge()}
            hasSwitchFrontendTemplate={
              challenge.isFrontendChallenge() &&
              !!challenge.frontendStyleConfig?.allowedTemplateSeriesCodes
            }
            hasVersionCommand={
              challengeContext.settings &&
              challengeContext.settings.versionCommand &&
              challengeContext.settings.versionCommand.length > 0
            }
            isChallengeInProgress={exam.canChallengeSubmit(challengeId)}
            isUsingVsCodeEditor={ChallengeStyle.shouldUseVsCodeEditor(
              challenge.style,
            )}
            onSubmit={this.onPrepareChallengeSubmit}
            onReset={this.onOpenReset}
            onSwitchProgrammingLanguage={this.onOpenSwitchLanguage}
            onSwitchFrontendTemplate={this.onOpenSwitchFrontendTemplate}
            onVersionCommand={this.onVersionCommand}
            onShowTestInformation={this.onShowTestInformation}
            onSwitchTakenBy={this.onOpenSwitchTakenBy}
            onInitializeRemote={this.onInitializeRemote}
            updatedAt={challengeResult.updatedAt}
            takenBy={challengeResult.takenBy}
            isOpenAppealComment={this.state.isOpenAppealComment}
            onShowAppealComment={this.onShowAppealComment}
          />
        </div>
        {challengeFinished}
        <Split sizes={[75, 26]} className={editorClassName}>
          <div className="code-editor-main__editor">
            <iframe
              id="code-editor-iframe"
              className="code-editor-main__iframe"
              src={challenge.getEditorURL()}
              ref={(iframe) => (this.iframe = iframe)}
            />
            <HotkeyHandler
              {...getScreenshotHotKeys(bowser.getOSName(true))}
              onHotkeyDetected={this.onScreenshotHotkeyDetected}
              iframeDocument={this.iframe?.contentDocument || undefined}
            />
          </div>
          <div
            className="code-editor-main__appeal-form"
            aria-hidden={!this.state.isOpenAppealComment}
          >
            <div className={appealTitleClassName}>
              <a
                aria-label="Close Appeal Comment"
                aria-pressed={this.state.isOpenAppealComment}
                onClick={() => this.onCloseAppealComment()}
              >
                <Msg id="close" />
                <Icon type="times-circle" />
              </a>
            </div>
            {this.state.isOpenAppealComment && (
              <EditorAppealForm
                value={this.state.appealComment}
                isUpdating={isUpdatingAppealComment}
                isUpdateFail={isUpdateAppealCommentError}
                isUpdateSuccess={isUpdateAppealCommentSuccess}
                onChange={(value) => {
                  this.setState({ appealComment: value });
                  this.onUpdateAppealComment();
                }}
              />
            )}
          </div>
        </Split>
        {ChallengeStyle.hasLocalExam(challenge.style) &&
          challengeResult.takenBy === ChallengeTakenBy.LocalMachine && (
            <div className="code-editor-main__cli-footer">
              <Tooltip
                text={Message.getMessageByKey("editor-exam-detail.clone.text")}
                maxSize="large"
              >
                <CopyBox value={"track clone " + window.location.href} />
              </Tooltip>
              <Tooltip text={Message.getMessageByKey("editor.cli.document")}>
                <JumpTo to={Message.getMessageByKey("editor.link.coding.cli")}>
                  <Icon type={"info-circle"} />
                </JumpTo>
              </Tooltip>
              <div
                className={`code-editor-main__code-updated ${
                  this.state.isShowCodeUpdated ? "is-show" : ""
                }`}
              >
                <div className="code-editor-main__code-updated__inner">
                  <Icon type={"exclamation-triangle"} iconClassName={"fa-4x"} />
                  <p>
                    <Msg id={"message.code.updated"} />
                  </p>
                  <p>
                    <Msg id={"editor.reload.toUpdate"} />
                  </p>
                </div>
                <Button
                  type={"primary"}
                  onClick={() => window.location.reload()}
                  ariaLabel="Reload"
                >
                  <Msg id={"action.reload"} />
                </Button>
              </div>
            </div>
          )}
        <SetEnvironment
          resultId={challengeResult.id}
          variables={challengeResult.variables}
          isOpen={isOpenEnvVars}
          onCancel={this.onCloseModal}
          onUpdate={this.onCloseModal}
          updateEnvVars={updateEnvVars}
        />
        {/* // TTODO: Refactor this once the isWebcamEnabled feature flag is removed */}
        {isOpenStart && !this.props.isWebcamEnabled && (
          <StartChallenge
            isOpen={isOpenStart}
            challenge={challenge}
            challengeResult={challengeResult}
            onStart={this.onStartChallenge}
            onCancel={this.onCancelStartChallenge}
          />
        )}
        {isOpenStart && this.props.isWebcamEnabled && (
          <StartChallengeWebcam
            isOpen={isOpenStart}
            challenge={challenge}
            challengeResult={challengeResult}
            hasVerifiedLiveness={hasVerifiedLiveness}
            onStart={this.onStartChallenge}
            onCancel={this.onCancelStartChallenge}
          />
        )}
        <SwitchLanguage
          isOpen={isOpenSwitchLanguage}
          switchLanguage={switchLanguage}
          programmingLanguages={challenge.programmingLanguages}
          programmingLanguage={challengeResult.programmingLanguage}
          resultId={challengeResult.id}
          onSwitch={this.onSwitchLanguage}
          onCancel={this.onCloseModal}
        />
        <SwitchFrontendTemplate
          isOpen={isOpenSwitchFrontendTemplate}
          onSwitchFrontendTemplate={this.onSwitchFrontendTemplate}
          selectedFrontendTemplate={challengeContext.selectedFrontendTemplate}
          allowedFrontendTemplates={
            challenge.frontendStyleConfig?.allowedTemplateSeriesCodes ?? []
          }
          onCancel={this.onCloseModal}
        />
        <SwitchTakenBy
          isOpen={this.state.isOpenSwitchTakenBy}
          takenBy={this.state.switchTakenByTo}
          onSwitch={this.onSwitchTakenBy}
          onCancel={this.onCancelSwitchTakenBy}
        />
        <ResetChallenge
          isOpen={isOpenReset}
          onReset={this.onResetChallenge}
          onCancel={this.onCloseModal}
        />
        <SubmitChallenge
          isOpen={isOpenSubmit}
          type={challenge.style}
          onSubmit={this.onSubmitChallenge}
          onCancel={this.onCloseModal}
          onFinishSubmitChallenge={this.onFinishSubmitChallenge}
          score={this.state.testResult && this.state.testResult.success}
          totalScore={this.state.testResult && this.state.testResult.total}
        />
        <TadaMini
          startAnimation={this.state.showTada}
          score={this.state.testResult && this.state.testResult.success}
          totalScore={this.state.testResult && this.state.testResult.total}
          onFinishAnimation={() => this.setState({ showTada: false })}
          isBlack={ChallengeStyle.shouldUseVsCodeEditor(challenge.style)}
        />
        <Completed
          isOpen={this.state.isOpenCompleted}
          type={this.state.showCompletedType}
          onClose={this.onCloseModal}
        />
        {canRecordKeyEvents(exam, challengeContext, challengeId) && (
          <KeyEventHandler />
        )}
        {canTakeWebcamScreenshots({
          exam,
          challengeId,
          hasVerifiedLiveness,
        }) && <WebcamEventHandler />}
      </div>
    );
  }

  private onOpenSwitchLanguage = () => {
    this.setState({ isOpenSwitchLanguage: true });
  };

  private onOpenSwitchFrontendTemplate = () => {
    this.setState({ isOpenSwitchFrontendTemplate: true });
  };

  private onOpenReset = () => {
    this.setState({ isOpenReset: true });
  };

  private onShowTestInformation = () => {
    if (this.challengeEditor) {
      this.challengeEditor.showTestInformation();
    }
  };

  private onInitializeRemote = () => {
    if (this.challengeEditor) {
      this.challengeEditor.initializeRemote();
    }
  };

  private onPrepareChallengeSubmit = () => {
    const { challengeId, exam } = this.props;
    const challenge = exam.getChallengeByChallengeId(challengeId);
    if (this.challengeEditor) {
      if ([ChallengeStyle.AI, ChallengeStyle.Quiz].includes(challenge.style)) {
        this.onOpenSubmit();
      } else {
        // Inquiry about if unsaved contents exist
        this.challengeEditor.hadUnSaved();
        this.setState({ askingUnsaved: true });
      }
    }
  };

  private onHasUnsaved = (data: MessageHasUnsaved) => {
    this.props.setEditorHasUnsaved(data);
    const { askingUnsaved } = this.state;
    if (!askingUnsaved) {
      return;
    }
    const { hasUnsaved } = data;
    if (hasUnsaved && this.challengeEditor) {
      this.challengeEditor.save();
    }
    this.onOpenSubmit();
    this.setState({ askingUnsaved: false });
  };

  private onOpenSubmit = () => {
    if (this.challengeEditor) {
      this.setState({ isOpenSubmit: true });
    }
  };

  private onStartChallenge = ({
    frontendTemplate,
    programmingLanguage,
    takenBy,
  }: {
    frontendTemplate?: string;
    programmingLanguage?: ProgrammingLanguage;
    takenBy?: ChallengeTakenBy;
  }) => {
    const { exam, challengeId, startChallenge } = this.props;

    const challenge = exam.getChallengeByChallengeId(challengeId);
    const challengeResult = exam.getResultByChallengeId(challengeId);

    startChallenge({
      challengeId,
      resultId: challengeResult.id,
      isPrepared:
        challengeResult.status === ChallengeResultStatus.Prepared ||
        challengeResult.status === ChallengeResultStatus.CanRestart,
      hasLanguageSwitch:
        challenge.isProgrammingChallenge() || challenge.isFrontendChallenge(),
      frontendTemplate,
      programmingLanguage,
      takenBy,
    });

    this.onCloseModal();
  };

  private onSubmitChallenge = () => {
    const { exam, submitChallenge, isSubmittingChallenge } = this.props;

    if (!isSubmittingChallenge) {
      this.setState({ isManualSubmit: true });
      submitChallenge.mutate({
        examId: exam.id,
      });
    }
  };

  private onFinishSubmitChallenge = (type: ChallengeStyle) => {
    const { exam, challengeId } = this.props;
    if (this.props.history) {
      this.props.history.push(
        ChallengeStyle.hasAppealComment(type)
          ? `/${exam.org.name}/exams/${exam.urlToken}/appeal/${challengeId}`
          : `/${exam.org.name}/exams/${exam.urlToken}/challenges`,
      );
    }
  };

  private onResetChallenge = () => {
    this.setState({ isReset: true }, () => {
      const { exam, challengeId } = this.props;

      const challenge = exam.getChallengeByChallengeId(challengeId);

      if (ChallengeStyle.shouldUseVsCodeEditor(challenge.style)) {
        const challengeResult =
          this.props.exam.getResultByChallengeId(challengeId);

        const payload = {
          seriesCode: challengeResult.codingTemplate?.templateCode ?? "",
        };

        // passing in the same template resets the editor
        this.props.switchFrontendTemplate(payload, challengeResult.id);
      } else if (this.challengeEditor) {
        this.challengeEditor.clearCache();
      }
    });
  };

  private onEditorCacheClearComplete = () => {
    if (this.state.isReset) {
      const { exam, challengeId } = this.props;
      this.props.resetChallenge(exam.getResultByChallengeId(challengeId).id);
      this.onCloseModal();
    }
    this.setState({ isReset: false });
  };

  private onCancelStartChallenge = () => {
    const { exam, tutorial, challengeId } = this.props;
    if (!this.props.history) {
      return;
    }

    const newRoute = `/${tutorial ? "tutorial" : exam.org.name}/exams/${
      exam.urlToken
    }/challenges`;
    const challenge = exam.getChallengeByChallengeId(challengeId);

    // if current challenge uses the new editor then full redirect to page
    if (ChallengeStyle.shouldUseVsCodeEditor(challenge.style)) {
      window.location.href = newRoute;
      return;
    }

    // otherwise just push the new route
    this.props.history.push(newRoute);
  };

  private onSwitchLanguage = () => {
    this.setState({
      isSwitchingLanguage: true,
    });
    this.onCloseModal();
  };

  private onSwitchFrontendTemplate = (payload: { seriesCode: string }) => {
    this.setState({
      isSwitchingLanguage: true,
    });

    const challengeResult = this.props.exam.getResultByChallengeId(
      this.props.challengeId,
    );

    this.props.switchFrontendTemplate(payload, challengeResult.id);
    this.onCloseModal();
  };

  private onVersionCommand = () => {
    const versionCommand =
      this.props.challengeContext?.settings.versionCommand || [];
    versionCommand.forEach((command) => {
      this.challengeEditor?.runCommand(command);
    });
  };

  private onCloseModal = () => {
    this.setState({
      isOpenEnvVars: false,
      isOpenSwitchLanguage: false,
      isOpenSwitchFrontendTemplate: false,
      isOpenStart: false,
      isOpenReset: false,
      isOpenSubmit: false,
      isOpenSwitchTakenBy: false,
      isOpenCompleted: false,
    });
  };

  private onShowAppealComment = () => {
    this.setState({
      isOpenAppealComment: true,
    });
  };

  private onCloseAppealComment = () => {
    this.setState({
      isOpenAppealComment: false,
    });
  };

  private onMovePage = (data: MessageMoved) => {
    const { exam, challengeId, tutorial } = this.props;
    const { page, showReview, historyReplace } = data;
    const url = `/${tutorial ? "tutorial" : exam.org.name}/exams/${
      exam.urlToken
    }/challenges/${challengeId}/${page}`;
    // replace history only when initial load
    historyReplace
      ? this.props.history.replace(url, { page, showReview, kind: "quiz" })
      : this.props.history.push(url, { page, showReview, kind: "quiz" });
  };

  private onTestResult = (testResult: TestResult) => {
    const { challengeId, exam } = this.props;

    sessionStorage.setItem(
      `${LAST_SCORE_KEY}.${exam.urlToken}.${challengeId}`,
      testResult,
    );

    if (testResult.highScore > this.state.testResult.highScore) {
      Logger.info(`:tada: You got a highScore!! ${testResult.highScore}`);
      this.setState({ testResult, showTada: true });
    } else {
      this.setState({ testResult });
    }
  };

  private onUpdateAppealComment = () => {
    this.props.updateAppealComment(
      { appealComment: this.state.appealComment },
      this.props.exam.getResultByChallengeId(this.props.challengeId).id,
    );
  };

  private onOpenSwitchTakenBy = ({
    takenBy,
  }: {
    takenBy: ChallengeTakenBy;
  }) => {
    this.setState({
      isOpenSwitchTakenBy: true,
      switchTakenByTo: takenBy,
    });
  };

  private onSwitchTakenBy = () => {
    this.props.switchTakenBy(
      { takenBy: this.state.switchTakenByTo },
      this.props.exam.getResultByChallengeId(this.props.challengeId).id,
    );
  };

  private onCancelSwitchTakenBy = () => {
    this.setState({ switchTakenByTo: ChallengeTakenBy.NotSelected }, () =>
      this.onCloseModal(),
    );
  };

  private onSaved = ({
    isCommit,
    isTestRun,
  }: {
    /**
     * true if something is actually saved
     */
    isCommit: boolean;
    /**
     * true if the run and save button gets clicked
     */
    isTestRun: boolean;
  }) => {
    if (isCommit || isTestRun) {
      this.props.saveChallenge({ isCommit, isTestRun });
    }
  };

  private tick = (timestamp: number) => {
    const { exam, challengeId } = this.props;

    if (timestamp - this.prevTick > 5000) {
      this.prevTick = timestamp;
      const challengeResult = exam.getResultByChallengeId(challengeId);
      if (
        exam.isChallengeStarted(challengeId) &&
        challengeResult.takenBy === ChallengeTakenBy.LocalMachine
      ) {
        this.props.getExam();
      }
    }

    this.animationFrameId = window.requestAnimationFrame(this.tick);
  };

  private onEditorNotifyError = (error: { message: string }) => {
    toast.error(error.message);
    this.setState({ askingUnsaved: false });
  };

  private onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (this.props.challengeEditorContext.hasUnsaved) {
      e.preventDefault();
      // This translated message may not show in some browsers
      // due to browser security but instead show a generic message instead
      e.returnValue = Message.getMessageByKey("navigation.confirm");
    }
  };

  private onPopState = (e: PopStateEvent) => {
    const state = e.state?.state;
    if (
      state &&
      "kind" in state &&
      "page" in state &&
      "showReview" in state &&
      state.kind === "quiz"
    ) {
      const { page, showReview } = state as {
        page: number;
        showReview: boolean;
      };
      this.challengeEditor?.movePage({ page, showReview });
    }
  };

  private onEditorPasted = ({
    text,
    filename,
  }: {
    text: string;
    filename: string;
  }) => {
    const {
      challengeContext: { challengeResultId },
    } = this.props;

    this.props.sendPasteEvent({
      resultId: challengeResultId,
      pastedText: text,
      filename,
    });
  };

  private onQuizEditorPasted = ({
    text,
    questionVersionId,
  }: {
    text: string;
    questionVersionId: number;
  }) => {
    const {
      challengeContext: { challengeResultId },
    } = this.props;

    this.props.sendPasteEvent({
      resultId: challengeResultId,
      questionVersionId,
      pastedText: text,
    });
  };

  private onScreenshotHotkeyDetected = (pressedKeys: string[]) => {
    this.challengeEditor?.showScreenshotPreventionAlert();
    this.props.sendScreenshotEvent.mutate({ pressedKeys });
  };

  private handleIntercomPositionUpdate = () => {
    let vertical_padding = 20;

    if (this.props.challengeId) {
      const { exam } = this.props;
      const challenge = exam.getChallengeByChallengeId(this.props.challengeId);

      if (ChallengeStyle.shouldUseVsCodeEditor(challenge.style)) {
        vertical_padding = 70;
      }
    }

    intercom.updateIntercomInfo({
      vertical_padding,
    });
  };
}

export default withRouter(EditorMain);
